import { zodResolver } from '@hookform/resolvers/zod';
import { useI18n } from 'hooks/useI18n';
import { useToasts } from 'hooks/useToasts';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { ServiceDeal } from 'services/service.deal';
import ServiceEventTracking from 'services/service.event-tracking';
import {
  CompaniesFormFieldsValidationSchema,
  FORM_FIELDS_DEFAULT_VALUES
} from './HomeV2Form.constants';
import { CompaniesFormFields, UseCompaniesForm } from './HomeV2Form.types';

export const useCompaniesForm: UseCompaniesForm = () => {
  const { t } = useI18n('home-v2');
  const { locale } = useRouter();
  const { addErrorToast, addSuccessToast } = useToasts();

  const formMethods = useForm<CompaniesFormFields>({
    defaultValues: FORM_FIELDS_DEFAULT_VALUES,
    resolver: zodResolver(CompaniesFormFieldsValidationSchema)
  });

  const isTermsAccepted = !!formMethods.watch('terms');

  const onSubmit = useCallback(
    async (data: CompaniesFormFields) => {
      try {
        await ServiceDeal.create({
          ...data,
          howDoYouKnowUs: data.howDoYouKnowUs || 'OTHER',
          source: 'Manfred Web',
          consultingOptions: []
        });

        formMethods.reset(FORM_FIELDS_DEFAULT_VALUES);
        addSuccessToast(t('home-v2.form.success'));
      } catch (ex) {
        console.error(
          `There was an error while submitting the Companies form. Form data: ${data}. Exception: ${ex}`
        );

        addErrorToast(t('home-v2.form.error'));
      }
    },
    [addErrorToast, addSuccessToast, formMethods, t]
  );

  const onFormFieldFocus = useCallback(() => {
    ServiceEventTracking.track({
      type: 'Landing - Form Focused',
      properties: {
        objectName: 'contacto-empresas',
        url: window.location.href,
        urlParams: new URLSearchParams(window.location.search).toString()
      }
    });
  }, []);

  return {
    t,
    locale,
    formMethods,
    onSubmit,
    isTermsAccepted,
    onFormFieldFocus
  };
};
